import React, { CSSProperties, useEffect, useRef } from 'react';
import * as yup from 'yup';
import { useFormContext } from './FormContext';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useModalContext } from '../../Modules/Modal/Services/ModalContext';

interface FormProps<T = any> {
	children?: React.ReactNode;
	defaultValues?: T;
	schema?: yup.ObjectSchema<any>;
	className?: any;
	style?: CSSProperties | undefined;
	registerForChanges?: boolean;
	notMonitoredValues?: string[];
	onChange?: (data: T) => void;
}
const Form = <T extends object = any>(props: FormProps<T>) => {
	const {
		children,
		defaultValues,
		schema,
		className,
		style,
		registerForChanges = false,
		notMonitoredValues,
		onChange,
	} = props;
	const modalContext = useModalContext();

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: schema ? yupResolver(schema) : undefined,
	});

	const { setFormMethods } = useFormContext();

	const lastResetValues = useRef<string | undefined>(undefined);
	const isFirstReset = useRef(true);
	useEffect(() => {
		const serialized = JSON.stringify(defaultValues ?? null);
		if (lastResetValues.current === serialized) return;
		lastResetValues.current = serialized;

		if (isFirstReset.current) {
			isFirstReset.current = false;
			formMethods.reset(defaultValues);
			return;
		}
		formMethods.reset(defaultValues, { keepDirtyValues: true });
	}, [defaultValues]);

	useEffect(() => {
		if (typeof setFormMethods === 'function') {
			setFormMethods(formMethods);
		}
	}, [formMethods]);

	useEffect(() => {
		if (registerForChanges) {
			setTimeout(() => {
				modalContext?.registerForm(() => formMethods, notMonitoredValues);
			}, 500);
		}
		return () => {
			if (registerForChanges) {
				setTimeout(() => {
					modalContext?.unregisterForm(() => formMethods);
				}, 500);
			}
		};
	}, []);

	const values = formMethods.watch();
	useEffect(() => {
		if (onChange) {
			onChange(formMethods.getValues() as T);
		}
	}, [JSON.stringify(values)]);

	return (
		<FormProvider {...formMethods}>
			<form action='' className={className} style={style}>
				{children}
			</form>
		</FormProvider>
	);
};

export default Form;
